import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import VariableContext from '../contexts/VariableContext';
import FetchingFreq from '../contexts/FetchingFreq';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/apiService';
import analysisTypes, { CATEGORIES } from './DataAnalysisParts/analysisTypes';
import TechniqueLibrary from './DataAnalysisParts/TechniqueLibrary';
import SelectedVariablesPool from './DataAnalysisParts/SelectedVariablesPool';
import AnalysisParamsForm, { validateParams, buildParamsPayload, firstZoneName, checklistDefaults } from './DataAnalysisParts/AnalysisParamsForm';
import AnalysisResults from './DataAnalysisParts/AnalysisResults';
import AnalysisHistory from './DataAnalysisParts/AnalysisHistory';
import SyntaxModal from './DataAnalysisParts/SyntaxModal';
import { generateSyntax } from './DataAnalysisParts/generateSyntax';
import DataPrepBuilder from './DataAnalysisParts/DataPrepBuilder';
import DataPrepRecipeStrip from './DataAnalysisParts/DataPrepRecipeStrip';
import { stepsToPreparedVariables, preparedVariablesToDataGroupEntries } from './DataAnalysisParts/dataPrepOps';
import './styles/DataAnalysis.css';

function firstReadyIdInCategory(category) {
  const found = analysisTypes.find((a) => a.category === category && a.status === 'ready');
  return found ? found.id : analysisTypes.find((a) => a.category === category)?.id;
}

function DataAnalysis() {
  const {
    dataGroup, projectId,
    currentSelectedVariables, setCurrentSelectedVariables,
    setcurrentSelectedVariablesset,
  } = useContext(VariableContext);
  const { setActivePage } = useContext(FetchingFreq);
  const { toast } = useToast();

  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);
  const [selectedTypeId, setSelectedTypeId] = useState(firstReadyIdInCategory(CATEGORIES[0]));
  const [activeZoneName, setActiveZoneName] = useState(null);
  const [values, setValues] = useState({});
  const [variableTypes, setVariableTypes] = useState(null);
  const [result, setResult] = useState(null);
  const [runLabel, setRunLabel] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [syntaxText, setSyntaxText] = useState(null);
  // Cross-cutting: NOT reset by resetConfig / technique switches, unlike `values`.
  const [dataPrepSteps, setDataPrepSteps] = useState([]);

  useEffect(() => { setActivePage("analysis"); }, [setActivePage]);

  useEffect(() => {
    if (!projectId) return;
    apiService.get(`/analysis/variable-types/${projectId}`)
      .then(({ data }) => setVariableTypes(data))
      .catch((err) => {
        console.error("Failed to load variable types:", err);
        // Without this, every variable falls back to a type-name heuristic that
        // can't tell a numeric rating scale from a nominal category, and DV/IV
        // zones would silently look unselectable with no indication why.
        toast.error("Couldn't load variable types — numeric/categorical detection may be inaccurate. Try refreshing.");
      });
  }, [projectId, toast]);

  const selectedType = useMemo(
    () => analysisTypes.find((a) => a.id === selectedTypeId) || analysisTypes[0],
    [selectedTypeId]
  );

  // Data Prep recipe steps produce new columns (recoded/stacked variables) that
  // aren't real survey questions. We synthesize dataGroup-shaped entries for
  // them so the existing variable-picker/role-zone code (which only knows how
  // to read dataGroup[key].varName) works on them completely unmodified.
  const preparedVariables = useMemo(
    () => stepsToPreparedVariables(dataPrepSteps, dataGroup || {}, variableTypes),
    [dataPrepSteps, dataGroup, variableTypes]
  );
  const effectiveDataGroup = useMemo(
    () => ({ ...(dataGroup || {}), ...preparedVariablesToDataGroupEntries(preparedVariables) }),
    [dataGroup, preparedVariables]
  );
  const effectiveSelectedKeys = useMemo(
    () => [...(currentSelectedVariables || []), ...preparedVariables.map((v) => v.name)],
    [currentSelectedVariables, preparedVariables]
  );

  const resetConfig = (type) => {
    setValues(checklistDefaults(type));
    setActiveZoneName(firstZoneName(type));
    setError(null);
    setResult(null);
  };

  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    const id = firstReadyIdInCategory(cat);
    setSelectedTypeId(id);
    resetConfig(analysisTypes.find((a) => a.id === id));
  };

  const handleSelectType = (id) => {
    setSelectedTypeId(id);
    resetConfig(analysisTypes.find((a) => a.id === id));
  };

  const handleParamChange = (name, val) => {
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const handleClearVariables = () => {
    setCurrentSelectedVariables([]);
    setcurrentSelectedVariablesset([]);
    resetConfig(selectedType);
  };

  const handleToggleVariable = (key) => {
    const zoneParam = selectedType.params.find((p) => p.name === activeZoneName);
    if (!zoneParam) return;
    if (zoneParam.multiple) {
      setValues((prev) => {
        const current = prev[zoneParam.name] || [];
        const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
        return { ...prev, [zoneParam.name]: next };
      });
    } else {
      setValues((prev) => ({ ...prev, [zoneParam.name]: prev[zoneParam.name] === key ? "" : key }));
    }
  };

  const runAnalysis = useCallback(async () => {
    if (!projectId) { setError("No project selected."); return; }
    const validationError = validateParams(selectedType, values, effectiveDataGroup);
    if (validationError) { setError(validationError); toast.warning(validationError); return; }

    setRunning(true);
    setError(null);
    try {
      const params = { ...buildParamsPayload(selectedType, values, effectiveDataGroup), dataPrep: dataPrepSteps };
      const engineType = selectedType.engineId || selectedType.id;
      const { data } = await apiService.post('/analysis/run', {
        projectId,
        analysisType: engineType,
        params,
        label: selectedType.label,
      });
      setResult(data);
      setRunLabel(selectedType.label);
      setHistoryRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err.message || "Analysis failed.");
      toast.error(err.message || "Analysis failed.");
    } finally {
      setRunning(false);
    }
  }, [projectId, selectedType, values, effectiveDataGroup, dataPrepSteps, toast]);

  const handleSelectHistoryRun = (run) => {
    const type = analysisTypes.find((a) => (a.engineId || a.id) === run.analysisType) || selectedType;
    setResult({ analysisType: run.analysisType, ...run.result });
    setRunLabel(run.label);
    setSelectedTypeId(type.id);
    setSelectedCategory(type.category);
  };

  const handlePasteSyntax = () => {
    setSyntaxText(generateSyntax(selectedType, values, effectiveDataGroup));
  };

  const handleAddPrepStep = (step) => setDataPrepSteps((prev) => [...prev, step]);
  const handleRemovePrepStep = (index) => setDataPrepSteps((prev) => prev.filter((_, i) => i !== index));
  const handleClearPrepRecipe = () => setDataPrepSteps([]);

  const handleCopySyntax = async () => {
    try {
      await navigator.clipboard.writeText(syntaxText || "");
      toast.success("Syntax copied to clipboard.");
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  const handleInsertToReport = async () => {
    if (!result) return;
    const lines = [`### ${runLabel}`, "", result.narrative || ""];
    if (Array.isArray(result.table) && result.table.length) {
      const cols = Object.keys(result.table[0]);
      lines.push("", `| ${cols.join(" | ")} |`, `| ${cols.map(() => "---").join(" | ")} |`);
      result.table.forEach((row) => lines.push(`| ${cols.map((c) => row[c]).join(" | ")} |`));
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Result summary copied — paste into your report.");
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  const activeZoneParam = selectedType.params.find((p) => p.name === activeZoneName);
  const activeZoneInfo = activeZoneParam
    ? { label: activeZoneParam.zone || activeZoneParam.label, role: activeZoneParam.role, multiple: activeZoneParam.multiple }
    : null;
  const assignedKeysForActiveZone = activeZoneParam
    ? (activeZoneParam.multiple ? (values[activeZoneParam.name] || []) : (values[activeZoneParam.name] ? [values[activeZoneParam.name]] : []))
    : [];

  const categoryItems = analysisTypes.filter((a) => a.category === selectedCategory);
  const displayOptions = values.statsAndPlots || {};

  return (
    <div className="analysis-workbench">
      <div className="analysis-category-tabs">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`analysis-category-tab ${cat === selectedCategory ? "active" : ""}`}
            onClick={() => handleSelectCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <DataPrepRecipeStrip
        projectId={projectId}
        steps={dataPrepSteps}
        onRemoveStep={handleRemovePrepStep}
        onClear={handleClearPrepRecipe}
      />

      <div className="analysis-workbench-body">
        <aside className="analysis-sidebar">
          <SelectedVariablesPool
            dataGroup={effectiveDataGroup}
            variableTypes={variableTypes}
            selectedKeys={effectiveSelectedKeys}
            activeZone={activeZoneInfo}
            assignedKeys={assignedKeysForActiveZone}
            onToggle={handleToggleVariable}
            onClear={handleClearVariables}
          />

          <TechniqueLibrary
            items={categoryItems}
            selectedId={selectedTypeId}
            runningId={running ? selectedTypeId : null}
            onSelect={handleSelectType}
          />

          <button className="analysis-history-toggle" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? 'Hide' : 'Show'} Run History
          </button>
          {showHistory && (
            <AnalysisHistory projectId={projectId} refreshKey={historyRefreshKey} onSelectRun={handleSelectHistoryRun} />
          )}
        </aside>

        <main className="analysis-main">
          <div className="analysis-config-panel">
            <h3>{selectedType.label}</h3>
            <p className="analysis-type-desc-full">{selectedType.description}</p>
            {selectedType.status === 'comingSoon' ? (
              <div className="analysis-empty-state">
                This technique isn't available yet. Pick a technique from the Technique Library on the left.
              </div>
            ) : selectedType.isPrepStep ? (
              <DataPrepBuilder
                prepKind={selectedType.prepKind}
                dataGroup={effectiveDataGroup}
                variableTypes={variableTypes}
                currentSelectedVariables={currentSelectedVariables}
                onAddStep={handleAddPrepStep}
              />
            ) : (
              <>
                <AnalysisParamsForm
                  analysisType={selectedType}
                  dataGroup={effectiveDataGroup}
                  values={values}
                  onChange={handleParamChange}
                  activeZoneName={activeZoneName}
                  onSetActiveZone={setActiveZoneName}
                  onReset={() => resetConfig(selectedType)}
                  onPasteSyntax={handlePasteSyntax}
                />
                {error && <div className="analysis-error">{error}</div>}
                <button className="analysis-run-btn" onClick={runAnalysis} disabled={running}>
                  {running ? 'Running...' : `Run ${selectedType.label}`}
                </button>
              </>
            )}
          </div>

          <div className="analysis-results-panel">
            {result ? (
              <>
                <div className="analysis-results-actions">
                  <button className="analysis-export-btn" onClick={handleInsertToReport}>Insert to Report</button>
                </div>
                <AnalysisResults analysisType={result.analysisType} analysisLabel={runLabel} result={result} displayOptions={displayOptions} />
              </>
            ) : (
              <div className="analysis-empty-state">
                Choose an analysis type on the left, click questions in the app sidebar to select variables,
                assign them into a role below, and click Run.
              </div>
            )}
          </div>
        </main>
      </div>

      {syntaxText !== null && (
        <SyntaxModal syntax={syntaxText} onClose={() => setSyntaxText(null)} onCopy={handleCopySyntax} />
      )}
    </div>
  );
}

export default DataAnalysis;
